import React, { useEffect, useState } from 'react';
import { liveClassesAPI } from '../../services/api';

const emptyForm = {
    title: '',
    description: '',
    meeting_url: '',
    start_time: '',
    end_time: '',
    timezone: 'Asia/Kolkata',
    capacity: '',
    is_published: true,
    is_active: true,
    status: 'SCHEDULED',
};

const LiveClassManager = () => {
    const [classes, setClasses] = useState([]);
    const [form, setForm] = useState(emptyForm);
    const [editingId, setEditingId] = useState(null);
    const [loading, setLoading] = useState(false);

    const loadMine = async () => {
        try {
            const res = await liveClassesAPI.getMine();
            setClasses(res.data || []);
        } catch (error) {
            console.error('Failed to fetch live classes', error);
            setClasses([]);
        }
    };

    useEffect(() => {
        loadMine();
    }, []);

    const onChange = (key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const payload = {
                ...form,
                capacity: form.capacity ? Number(form.capacity) : null,
                start_time: new Date(form.start_time).toISOString(),
                end_time: form.end_time ? new Date(form.end_time).toISOString() : null,
            };

            if (editingId) {
                await liveClassesAPI.update(editingId, payload);
                alert('Live class updated successfully');
            } else {
                await liveClassesAPI.create(payload);
                alert('Live class created successfully');
            }

            setEditingId(null);
            setForm(emptyForm);
            await loadMine();
        } catch (error) {
            alert(error.userMessage || 'Unable to save live class.');
        } finally {
            setLoading(false);
        }
    };

    const onEdit = (item) => {
        setEditingId(item.id);
        setForm({
            title: item.title || '',
            description: item.description || '',
            meeting_url: item.meeting_url || '',
            start_time: item.start_time ? new Date(item.start_time).toISOString().slice(0, 16) : '',
            end_time: item.end_time ? new Date(item.end_time).toISOString().slice(0, 16) : '',
            timezone: item.timezone || 'Asia/Kolkata',
            capacity: item.capacity || '',
            is_published: !!item.is_published,
            is_active: !!item.is_active,
            status: item.status || 'SCHEDULED',
        });
    };

    const onDelete = async (id) => {
        if (!window.confirm('Delete this live class?')) return;
        try {
            await liveClassesAPI.remove(id);
            await loadMine();
        } catch (error) {
            alert(error.userMessage || 'Unable to delete live class.');
        }
    };

    return (
        <div style={styles.page}>
            <div className="container" style={styles.container}>
                <h1 style={styles.title}>Manage Live Classes</h1>
                <p style={styles.subtitle}>Create and control all your live class sessions.</p>

                <form onSubmit={onSubmit} style={styles.form}>
                    <input value={form.title} onChange={(e) => onChange('title', e.target.value)} placeholder="Class title" required style={styles.input} />
                    <textarea value={form.description} onChange={(e) => onChange('description', e.target.value)} placeholder="Description" style={styles.input} />
                    <input value={form.meeting_url} onChange={(e) => onChange('meeting_url', e.target.value)} placeholder="Meeting URL" required style={styles.input} />
                    <input type="datetime-local" value={form.start_time} onChange={(e) => onChange('start_time', e.target.value)} required style={styles.input} />
                    <input type="datetime-local" value={form.end_time} onChange={(e) => onChange('end_time', e.target.value)} style={styles.input} />
                    <input value={form.timezone} onChange={(e) => onChange('timezone', e.target.value)} placeholder="Timezone" style={styles.input} />
                    <input type="number" value={form.capacity} onChange={(e) => onChange('capacity', e.target.value)} placeholder="Capacity" style={styles.input} />
                    <select value={form.status} onChange={(e) => onChange('status', e.target.value)} style={styles.input}>
                        <option value="SCHEDULED">🗓️ Scheduled</option>
                        <option value="LIVE_NOW">🔴 Live Now</option>
                        <option value="COMPLETED">✅ Completed (VOD)</option>
                    </select>
                    <button disabled={loading} type="submit" style={styles.primaryBtn}>{loading ? 'Saving...' : editingId ? 'Update Live Class' : 'Create Live Class'}</button>
                </form>

                <div style={styles.listWrap}>
                    {classes.map((item) => (
                        <div key={item.id} style={styles.item}>
                            <div>
                                <div style={styles.itemTitle}>
                                    {item.status === 'LIVE_NOW' && <span style={styles.liveBadge}>🔴 </span>}
                                    {item.status === 'COMPLETED' && <span style={styles.completedBadge}>✅ </span>}
                                    {item.title}
                                </div>
                                <div style={styles.itemMeta}>{new Date(item.start_time).toLocaleString()} • {item.timezone}</div>
                            </div>
                            <div style={styles.actions}>
                                <button type="button" onClick={() => onEdit(item)} style={styles.smallBtn}>Edit</button>
                                <button type="button" onClick={() => onDelete(item.id)} style={{ ...styles.smallBtn, ...styles.deleteBtn }}>Delete</button>
                            </div>
                        </div>
                    ))}
                    {classes.length === 0 && <div style={styles.empty}>No live classes created yet.</div>}
                </div>
            </div>
        </div>
    );
};

const styles = {
    page: { minHeight: '100vh', background: '#f8fafc', paddingTop: '90px' },
    container: { padding: '20px 16px 40px' },
    title: { margin: 0, fontSize: '36px', color: '#0f172a' },
    subtitle: { color: '#64748b', marginTop: '8px', marginBottom: '18px' },
    form: { display: 'grid', gap: '10px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '14px' },
    input: { border: '1px solid #cbd5e1', borderRadius: '8px', padding: '10px' },
    primaryBtn: { border: 'none', borderRadius: '8px', padding: '10px', background: '#16a34a', color: '#fff', fontWeight: '700', cursor: 'pointer' },
    listWrap: { marginTop: '16px', display: 'grid', gap: '10px' },
    item: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    itemTitle: { fontWeight: '700', color: '#0f172a' },
    liveBadge: { color: '#ef4444', fontSize: '12px', verticalAlign: 'middle' },
    completedBadge: { color: '#16a34a', fontSize: '12px', verticalAlign: 'middle' },
    itemMeta: { color: '#64748b', fontSize: '13px' },
    actions: { display: 'flex', gap: '8px' },
    smallBtn: { border: '1px solid #cbd5e1', borderRadius: '8px', background: '#fff', padding: '6px 10px', cursor: 'pointer' },
    deleteBtn: { color: '#dc2626', borderColor: '#fecaca' },
    empty: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px', color: '#64748b' },
};

export default LiveClassManager;
